// src/pages/Stats.js

'use strict';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import api from '../api/api';

import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  AccessTime as AccessTimeIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Apartment as ApartmentIcon,
  Block as BlockIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Dashboard as DashboardIcon,
  Dns as DnsIcon,
  ErrorOutline as ErrorOutlineIcon,
  FolderOpen as FolderOpenIcon,
  History as HistoryIcon,
  Memory as MemoryIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Storage as StorageIcon,
  TrendingUp as TrendingUpIcon,
  VpnKey as VpnKeyIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';

/* ========================================================================== */
/* Helpers                                                                    */
/* ========================================================================== */

const RANGE_OPTIONS = [
  { label: 'Última hora', value: '1 hour' },
  { label: 'Últimas 6 horas', value: '6 hours' },
  { label: 'Últimas 24 horas', value: '24 hours' },
  { label: 'Últimos 7 días', value: '7 days' },
];

const CPU_SAMPLE_OPTIONS = [100, 250, 500, 1000];

const STORAGE_LIMIT_OPTIONS = [5, 10, 15, 20, 30];

const STATUS_META = {
  ok: {
    label: 'Operativo',
    color: 'success',
    hex: '#2e7d32',
    icon: <CheckCircleOutlineIcon fontSize="small" />,
  },
  warning: {
    label: 'Atención',
    color: 'warning',
    hex: '#ed6c02',
    icon: <WarningAmberIcon fontSize="small" />,
  },
  critical: {
    label: 'Crítico',
    color: 'error',
    hex: '#d32f2f',
    icon: <ErrorOutlineIcon fontSize="small" />,
  },
  unknown: {
    label: 'Sin datos',
    color: 'default',
    hex: '#64748b',
    icon: <ErrorOutlineIcon fontSize="small" />,
  },
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const num = (value) => {
  const result = Number(value);
  return Number.isFinite(result) ? result : 0;
};

const clamp = (value, min = 0, max = 100) =>
  Math.max(min, Math.min(max, num(value)));

const fmtInt = (value) =>
  new Intl.NumberFormat('es-CL').format(Math.round(num(value)));

const fmtDecimal = (value, decimals = 2) =>
  new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(num(value));

const fmtPct = (value, decimals = 0) =>
  `${fmtDecimal(clamp(value), decimals)}%`;

function fmtBytes(value) {
  const bytes = Math.max(0, num(value));

  if (!bytes) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let unitIndex = 0;
  let amount = bytes;

  while (amount >= 1024 && unitIndex < units.length - 1) {
    amount /= 1024;
    unitIndex += 1;
  }

  return `${amount.toFixed(unitIndex >= 3 ? 2 : 1)} ${units[unitIndex]}`;
}

function fmtUptime(value) {
  const seconds = Math.max(0, Math.floor(num(value)));
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function fmtDateTime(value) {
  if (!value) return '—';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat('es-CL', {
    dateStyle: 'medium',
    timeStyle: 'medium',
  }).format(date);
}

function getStatusMeta(status) {
  return STATUS_META[status] || STATUS_META.unknown;
}

function getProgressColor(status) {
  const color = getStatusMeta(status).color;
  return color === 'default' ? 'primary' : color;
}

function prettyLabel(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getWorstStatus(statuses = []) {
  const weight = {
    ok: 0,
    warning: 1,
    critical: 2,
    unknown: -1,
  };

  return statuses.reduce((worst, current) => {
    const currentWeight = weight[current] ?? -1;
    const worstWeight = weight[worst] ?? -1;

    return currentWeight > worstWeight ? current : worst;
  }, 'ok');
}

/* ========================================================================== */
/* Componentes visuales                                                       */
/* ========================================================================== */

function StatusChip({ status, label, variant = 'filled' }) {
  const meta = getStatusMeta(status);

  return (
    <Chip
      size="small"
      icon={meta.icon}
      label={label || meta.label}
      color={meta.color}
      variant={variant}
      sx={{
        fontWeight: 800,
        '& .MuiChip-icon': {
          ml: 0.8,
        },
      }}
    />
  );
}

function SectionTitle({ icon, title, description, action }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      alignItems={{ sm: 'center' }}
      justifyContent="space-between"
      sx={{ mb: 1.5 }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Avatar
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            bgcolor: 'rgba(15, 23, 42, 0.08)',
            color: '#0f172a',
          }}
        >
          {icon}
        </Avatar>

        <Box>
          <Typography variant="h6" fontWeight={900}>
            {title}
          </Typography>

          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>
      </Stack>

      {action}
    </Stack>
  );
}

function MetricCard({
  icon,
  title,
  value,
  subtitle,
  status = 'ok',
  progress,
  footer,
  children,
}) {
  const meta = getStatusMeta(status);

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 3,
        border: '1px solid rgba(15, 23, 42, 0.10)',
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ p: 2.2 }}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <Avatar
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              bgcolor: `${meta.hex}14`,
              color: meta.hex,
              flexShrink: 0,
            }}
          >
            {icon}
          </Avatar>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              spacing={1}
              alignItems="flex-start"
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 900, color: 'text.secondary' }}
              >
                {title}
              </Typography>

              <StatusChip status={status} variant="outlined" />
            </Stack>

            <Typography
              variant="h5"
              sx={{
                mt: 0.5,
                fontWeight: 900,
                lineHeight: 1.15,
                wordBreak: 'break-word',
              }}
            >
              {value}
            </Typography>

            {subtitle && (
              <Typography
                variant="body2"
                sx={{ mt: 0.7, color: 'text.secondary' }}
              >
                {subtitle}
              </Typography>
            )}

            {progress !== undefined && progress !== null && (
              <LinearProgress
                variant="determinate"
                value={clamp(progress)}
                color={getProgressColor(status)}
                sx={{
                  mt: 1.5,
                  height: 8,
                  borderRadius: 99,
                  bgcolor: 'rgba(15, 23, 42, 0.08)',
                }}
              />
            )}

            {children && <Box sx={{ mt: 1.5 }}>{children}</Box>}

            {footer && (
              <Typography
                variant="caption"
                sx={{
                  mt: 1.25,
                  display: 'block',
                  color: 'text.secondary',
                }}
              >
                {footer}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function ProcessTable({ title, rows, order }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid rgba(15, 23, 42, 0.10)',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={900}>
          {title}
        </Typography>

        <Typography variant="caption" color="text.secondary">
          Procesos con mayor consumo actual.
        </Typography>
      </Box>

      <Divider />

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Proceso</TableCell>
              <TableCell align="right">PID</TableCell>
              <TableCell align="right">
                {order === 'cpu' ? 'CPU' : 'Memoria'}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary">
                    No hay datos de procesos disponibles.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {rows.map((row) => {
              const usage =
                order === 'cpu'
                  ? fmtPct(row.cpu_percent, 1)
                  : fmtPct(row.memory_percent, 1);

              return (
                <TableRow key={`${order}-${row.pid}-${row.command}`}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={800}>
                      {row.command || '—'}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      {row.user || '—'}
                    </Typography>
                  </TableCell>

                  <TableCell align="right">{fmtInt(row.pid)}</TableCell>

                  <TableCell align="right">
                    <Chip
                      size="small"
                      label={usage}
                      color={
                        num(
                          order === 'cpu'
                            ? row.cpu_percent
                            : row.memory_percent
                        ) >= 80
                          ? 'warning'
                          : 'default'
                      }
                      variant="outlined"
                      sx={{ fontWeight: 800 }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

/* ========================================================================== */
/* Página                                                                      */
/* ========================================================================== */

export default function Stats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [softLoading, setSoftLoading] = useState(false);
  const [pageError, setPageError] = useState('');

  const [since, setSince] = useState('1 hour');
  const [cpuSampleMs, setCpuSampleMs] = useState(250);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const [storagePath, setStoragePath] = useState('');
  const [storageLimit, setStorageLimit] = useState(10);
  const [storageAnalysis, setStorageAnalysis] = useState(null);
  const [storageLoading, setStorageLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const requestInProgressRef = useRef(false);
  const hasLoadedRef = useRef(false);

  const fetchStats = useCallback(
    async ({ soft = false, silent = false } = {}) => {
      if (requestInProgressRef.current) return;

      requestInProgressRef.current = true;

      if (soft) setSoftLoading(true);
      else setLoading(true);

      if (!silent) setPageError('');

      try {
        const response = await api.get('/stats', {
          params: {
            since,
            topLimit: 10,
            cpuSampleMs,
          },
        });

        setStats(response.data);
      } catch (error) {
        console.error('[Stats] Error cargando panel:', error);

        const message =
          error.response?.data?.message ||
          'No fue posible cargar las métricas del sistema.';

        if (!silent) {
          setPageError(message);

          setSnackbar({
            open: true,
            message,
            severity: 'error',
          });
        }
      } finally {
        requestInProgressRef.current = false;
        setLoading(false);
        setSoftLoading(false);
      }
    },
    [since, cpuSampleMs]
  );

  const analyzeStorage = useCallback(async () => {
    if (!storagePath || storageLoading) return;

    setStorageLoading(true);

    try {
      const response = await api.get('/stats/topDirectories', {
        params: {
          path: storagePath,
          limit: storageLimit,
        },
      });

      const result = response.data?.topDirectories || null;

      setStorageAnalysis(result);

      if (result?.error) {
        setSnackbar({
          open: true,
          message: result.message || 'No fue posible analizar la ruta seleccionada.',
          severity: 'warning',
        });
      }
    } catch (error) {
      console.error('[Stats] Error analizando almacenamiento:', error);

      const message =
        error.response?.data?.message ||
        'No fue posible analizar los directorios.';

      setSnackbar({
        open: true,
        message,
        severity: 'error',
      });
    } finally {
      setStorageLoading(false);
    }
  }, [storageLimit, storageLoading, storagePath]);

  useEffect(() => {
    fetchStats({ soft: hasLoadedRef.current });
    hasLoadedRef.current = true;
  }, [fetchStats]);

  useEffect(() => {
    if (!autoRefresh) return undefined;

    const timer = window.setInterval(() => {
      fetchStats({ soft: true, silent: true });
    }, 30000);

    return () => window.clearInterval(timer);
  }, [autoRefresh, fetchStats]);

  const server = stats?.server || {};
  const monitoring = stats?.monitoring || {};
  const dbStats = stats?.db_stats || {};
  const analytics = stats?.analytics || {};
  const meta = stats?.meta || {};

  const memory = server.memory || {};
  const swap = memory.swap || {};
  const disk = server.disk || {};
  const cpu = server.cpu_usage || null;
  const load = server.load || {};
  const appProcess = server.process || {};

  const filesystems = asArray(server.filesystems);
  const topProcesses = server.top_processes || {};
  const allowedStoragePaths = asArray(monitoring.allowed_storage_paths);

  useEffect(() => {
    if (!allowedStoragePaths.length) return;

    const pathStillAllowed =
      storagePath && allowedStoragePaths.includes(storagePath);

    if (pathStillAllowed) return;

    const preferred =
      allowedStoragePaths.find((item) => item === '/home/debian/server') ||
      allowedStoragePaths[0];

    setStoragePath(preferred);
  }, [allowedStoragePaths, storagePath]);

  const overallStatus = monitoring.status || 'unknown';
  const overallMeta = getStatusMeta(overallStatus);

  const memoryUsedPercent = num(memory.used_percent);
  const memoryAvailablePercent = num(memory.available_percent);
  const swapUsedPercent = num(swap.used_percent);
  const diskUsedPercent = num(disk.used_percent);
  const inodeUsedPercent = num(disk.inode_used_percent);
  const cpuPercent = num(cpu?.usage_percent);
  const loadPerCpu = num(load.per_cpu_one);

  const cpuCores = asArray(cpu?.per_core);
  const processCpu = asArray(topProcesses.by_cpu);
  const processMemory = asArray(topProcesses.by_memory);

  const storageDirectories = asArray(storageAnalysis?.directories);

  const attentionItems = useMemo(() => {
    const result = [];

    if (cpu && cpu.status && cpu.status !== 'ok') {
      result.push({
        status: cpu.status,
        title: 'CPU elevada',
        text: `Uso promedio actual: ${fmtPct(cpu.usage_percent)}.`,
      });
    }

    if (memory.status && memory.status !== 'ok') {
      result.push({
        status: memory.status,
        title: 'Memoria bajo presión',
        text: `Memoria disponible: ${fmtPct(memory.available_percent)}.`,
      });
    }

    if (num(swap.total) > 0 && swap.status && swap.status !== 'ok') {
      result.push({
        status: swap.status,
        title: 'Uso de swap elevado',
        text: `Swap utilizada: ${fmtPct(swap.used_percent)}.`,
      });
    }

    if (load.status && load.status !== 'ok') {
      result.push({
        status: load.status,
        title: 'Carga elevada',
        text: `Carga por CPU: ${fmtDecimal(load.per_cpu_one, 2)}.`,
      });
    }

    const problematicFilesystems = filesystems.filter(
      (item) => item.status && item.status !== 'ok'
    );

    problematicFilesystems.slice(0, 3).forEach((item) => {
      result.push({
        status: item.status,
        title: `Partición con uso elevado: ${item.path}`,
        text: `Disco ${fmtPct(item.used_percent)} · Inodos ${fmtPct(
          item.inode_used_percent
        )}.`,
      });
    });

    if (storageAnalysis?.error) {
      result.push({
        status: 'warning',
        title: 'Análisis de directorios incompleto',
        text:
          storageAnalysis.message ||
          'La ruta no pudo analizarse completamente.',
      });
    }

    return result.sort((a, b) => {
      const priority = { critical: 2, warning: 1, ok: 0 };
      return (priority[b.status] || 0) - (priority[a.status] || 0);
    });
  }, [cpu, filesystems, load, memory, storageAnalysis, swap]);

  const tenantActivePercent =
    num(dbStats.tenants_total) > 0
      ? (num(dbStats.tenants_active) * 100) / num(dbStats.tenants_total)
      : 0;

  const userActivePercent =
    num(dbStats.users_total) > 0
      ? (num(dbStats.users_active) * 100) / num(dbStats.users_total)
      : 0;

  const dbTop = asArray(dbStats.databases_top);
  const topUsers = asArray(analytics.top_users);
  const tenantsWithoutUsers = asArray(analytics.tenants_without_users);

  const thresholdText = useMemo(() => {
    const thresholds = monitoring.thresholds || {};

    return [
      `CPU ${thresholds.cpu_warning_percent || 85}% / ${
        thresholds.cpu_critical_percent || 95
      }%`,
      `Disco ${thresholds.disk_warning_percent || 80}% / ${
        thresholds.disk_critical_percent || 90
      }%`,
      `RAM disponible ${thresholds.memory_available_warning_percent || 20}% / ${
        thresholds.memory_available_critical_percent || 10
      }%`,
      `Swap ${thresholds.swap_warning_percent || 50}% / ${
        thresholds.swap_critical_percent || 80
      }%`,
    ];
  }, [monitoring.thresholds]);

  if (loading && !stats) {
    return (
      <Box
        sx={{
          minHeight: 360,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Stack spacing={1.5} alignItems="center">
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Cargando monitoreo del sistema…
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (pageError && !stats) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => fetchStats()}>
              Reintentar
            </Button>
          }
        >
          {pageError}
        </Alert>
      </Container>
    );
  }

  if (!stats) return null;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={3}>
        {/* ================================================================= */}
        {/* Encabezado                                                         */}
        {/* ================================================================= */}

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.75 },
            borderRadius: 4,
            border: '1px solid rgba(15, 23, 42, 0.10)',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              opacity: 0.04,
              pointerEvents: 'none',
              backgroundImage:
                'linear-gradient(rgba(15,23,42,.35) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,.35) 1px, transparent 1px)',
              backgroundSize: '28px 28px',
            }}
          />

          <Stack
            spacing={2}
            sx={{
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              justifyContent="space-between"
              alignItems={{ lg: 'center' }}
              spacing={2}
            >
              <Box>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2.5,
                      bgcolor: `${overallMeta.hex}14`,
                      color: overallMeta.hex,
                    }}
                  >
                    <DashboardIcon />
                  </Avatar>

                  <Box>
                    <Typography variant="h4" fontWeight={900}>
                      Centro de monitoreo
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      Recursos del servidor, aplicación, base de datos y seguridad.
                    </Typography>
                  </Box>
                </Stack>

                <Stack
                  direction="row"
                  spacing={0.9}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mt: 1.5 }}
                >
                  <StatusChip
                    status={overallStatus}
                    label={`Estado: ${overallMeta.label}`}
                  />

                  <Chip
                    size="small"
                    icon={<DnsIcon />}
                    label={`Host: ${server.hostname || '—'}`}
                    variant="outlined"
                    sx={{ fontWeight: 800 }}
                  />

                  <Chip
                    size="small"
                    icon={<AccessTimeIcon />}
                    label={`Uptime: ${fmtUptime(server.uptime)}`}
                    variant="outlined"
                    sx={{ fontWeight: 800 }}
                  />

                  <Chip
                    size="small"
                    icon={<SpeedIcon />}
                    label={`Respuesta: ${fmtDecimal(meta.response_time_ms, 0)} ms`}
                    variant="outlined"
                    sx={{ fontWeight: 800 }}
                  />

                  {autoRefresh && (
                    <Chip
                      size="small"
                      icon={<RefreshIcon />}
                      label="Actualización automática cada 30 s"
                      color="info"
                      variant="outlined"
                      sx={{ fontWeight: 800 }}
                    />
                  )}
                </Stack>
              </Box>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'center' }}
              >
                <TextField
                  select
                  size="small"
                  label="Ventana de actividad"
                  value={since}
                  onChange={(event) => setSince(event.target.value)}
                  sx={{ minWidth: 180 }}
                >
                  {RANGE_OPTIONS.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Muestra CPU"
                  value={cpuSampleMs}
                  onChange={(event) => setCpuSampleMs(num(event.target.value))}
                  sx={{ minWidth: 145 }}
                >
                  {CPU_SAMPLE_OPTIONS.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item} ms
                    </MenuItem>
                  ))}
                </TextField>

                <FormControlLabel
                  sx={{
                    ml: { sm: 0 },
                    mr: 0,
                    whiteSpace: 'nowrap',
                  }}
                  control={
                    <Switch
                      size="small"
                      checked={autoRefresh}
                      onChange={(event) => setAutoRefresh(event.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={700}>
                      Auto
                    </Typography>
                  }
                />

                <Tooltip title="Actualizar ahora">
                  <span>
                    <IconButton
                      color="primary"
                      onClick={() => fetchStats({ soft: true })}
                      disabled={softLoading}
                      sx={{
                        border: '1px solid rgba(25, 118, 210, 0.20)',
                      }}
                    >
                      {softLoading ? (
                        <CircularProgress size={21} />
                      ) : (
                        <RefreshIcon />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>

            {softLoading && <LinearProgress />}

            {pageError && (
              <Alert
                severity="warning"
                onClose={() => setPageError('')}
                sx={{ borderRadius: 2 }}
              >
                {pageError}
              </Alert>
            )}

            <Typography variant="caption" color="text.secondary">
              Última lectura: {fmtDateTime(meta.timestamp)} · Ventana:{' '}
              {meta.since || since} · Caché rápido: 30 segundos.
            </Typography>
          </Stack>
        </Paper>

        {/* ================================================================= */}
        {/* Alertas                                                            */}
        {/* ================================================================= */}

        <Paper
          elevation={0}
          sx={{
            p: 2.25,
            borderRadius: 3,
            border: '1px solid rgba(15, 23, 42, 0.10)',
          }}
        >
          <SectionTitle
            icon={
              attentionItems.length ? (
                <WarningAmberIcon />
              ) : (
                <CheckCircleOutlineIcon />
              )
            }
            title={
              attentionItems.length
                ? 'Alertas y elementos a revisar'
                : 'Sin alertas activas'
            }
            description={
              attentionItems.length
                ? 'Se detectaron métricas fuera de los umbrales configurados.'
                : 'CPU, memoria, carga y almacenamiento están dentro de los umbrales.'
            }
          />

          {attentionItems.length === 0 ? (
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              El servidor se encuentra operativo según los umbrales actuales.
            </Alert>
          ) : (
            <Grid container spacing={1.25}>
              {attentionItems.map((item, index) => (
                <Grid item xs={12} md={6} lg={4} key={`${item.title}-${index}`}>
                  <Alert
                    severity={
                      item.status === 'critical'
                        ? 'error'
                        : item.status === 'warning'
                          ? 'warning'
                          : 'info'
                    }
                    sx={{ height: '100%', borderRadius: 2 }}
                  >
                    <Typography variant="body2" fontWeight={900}>
                      {item.title}
                    </Typography>

                    <Typography variant="caption">{item.text}</Typography>
                  </Alert>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>

        {/* ================================================================= */}
        {/* Recursos                                                           */}
        {/* ================================================================= */}

        <Box>
          <SectionTitle
            icon={<SpeedIcon />}
            title="Recursos del servidor"
            description="Uso actual de CPU, memoria, swap, carga y almacenamiento principal."
          />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                icon={<SpeedIcon />}
                title="CPU"
                value={cpu ? fmtPct(cpuPercent, 1) : '—'}
                subtitle={
                  cpu
                    ? `${fmtInt(cpuCores.length || server.cpus)} núcleos · muestra ${
                        cpu.sample_ms || cpuSampleMs
                      } ms`
                    : 'Métrica no disponible.'
                }
                status={cpu?.status || 'unknown'}
                progress={cpu ? cpuPercent : 0}
                footer={`Carga por CPU: ${fmtDecimal(loadPerCpu, 2)}`}
              />
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                icon={<MemoryIcon />}
                title="Memoria RAM"
                value={`${memory.available_h || fmtBytes(memory.available)} disponibles`}
                subtitle={`Usada: ${
                  memory.used_h || fmtBytes(memory.used)
                } de ${memory.total_h || fmtBytes(memory.total)}`}
                status={memory.status || 'unknown'}
                progress={memoryUsedPercent}
                footer={`Disponible: ${fmtPct(memoryAvailablePercent, 1)} · Libre real: ${
                  memory.free_h || fmtBytes(memory.free)
                }`}
              />
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                icon={<MemoryIcon />}
                title="Memoria swap"
                value={
                  num(swap.total) > 0
                    ? `${swap.used_h || fmtBytes(swap.used)} usada`
                    : 'No configurada'
                }
                subtitle={
                  num(swap.total) > 0
                    ? `Total: ${swap.total_h || fmtBytes(swap.total)} · Libre: ${
                        swap.free_h || fmtBytes(swap.free)
                      }`
                    : 'El sistema no reporta espacio de intercambio.'
                }
                status={
                  num(swap.total) > 0 ? swap.status || 'ok' : 'unknown'
                }
                progress={num(swap.total) > 0 ? swapUsedPercent : 0}
                footer={
                  num(swap.total) > 0
                    ? `Uso: ${fmtPct(swapUsedPercent, 1)}`
                    : 'Sin uso de swap.'
                }
              />
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                icon={<TrendingUpIcon />}
                title="Carga del sistema"
                value={`${fmtDecimal(load.one, 2)} / ${fmtDecimal(
                  load.five,
                  2
                )} / ${fmtDecimal(load.fifteen, 2)}`}
                subtitle="Promedios de carga: 1, 5 y 15 minutos."
                status={load.status || 'unknown'}
                progress={clamp(loadPerCpu * 50)}
                footer={`Carga por CPU: ${fmtDecimal(loadPerCpu, 2)} · ${
                  fmtInt(load.cpu_count || server.cpus)
                } núcleos`}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <MetricCard
                icon={<StorageIcon />}
                title={`Disco principal ${disk.path || '/'}`}
                value={disk.error ? 'No disponible' : fmtPct(diskUsedPercent, 1)}
                subtitle={
                  disk.error
                    ? disk.message || disk.error
                    : `Usado: ${disk.used_h || fmtBytes(disk.used)} de ${
                        disk.total_h || fmtBytes(disk.total)
                      } · Disponible: ${
                        disk.available_h || fmtBytes(disk.available)
                      }`
                }
                status={
                  disk.error
                    ? 'warning'
                    : getWorstStatus([
                        diskUsedPercent >= 90
                          ? 'critical'
                          : diskUsedPercent >= 80
                            ? 'warning'
                            : 'ok',
                        inodeUsedPercent >= 90
                          ? 'critical'
                          : inodeUsedPercent >= 80
                            ? 'warning'
                            : 'ok',
                      ])
                }
                progress={diskUsedPercent}
                footer={`Inodos usados: ${fmtPct(inodeUsedPercent, 1)} · Fuente: ${
                  disk.source || '—'
                }`}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <MetricCard
                icon={<DnsIcon />}
                title="Proceso IntegraNube API"
                value={`PID ${appProcess.pid || '—'}`}
                subtitle={`Node ${appProcess.node_version || '—'} · Uptime ${
                  appProcess.uptime_human || fmtUptime(appProcess.uptime_seconds)
                }`}
                status="ok"
                footer={`RSS: ${
                  appProcess.memory?.rss_h || fmtBytes(appProcess.memory?.rss)
                } · Heap: ${
                  appProcess.memory?.heap_used_h ||
                  fmtBytes(appProcess.memory?.heap_used)
                }`}
              />
            </Grid>
          </Grid>

          <Paper
            elevation={0}
            sx={{
              mt: 2,
              p: 2.25,
              borderRadius: 3,
              border: '1px solid rgba(15, 23, 42, 0.10)',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
              spacing={1}
              sx={{ mb: 1.75 }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={900}>
                  Uso por núcleo
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Permite detectar si un solo núcleo está concentrando la carga.
                </Typography>
              </Box>

              <StatusChip status={cpu?.status || 'unknown'} variant="outlined" />
            </Stack>

            {!cpuCores.length ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No hay detalle por núcleo disponible.
              </Alert>
            ) : (
              <Grid container spacing={1.25}>
                {cpuCores.map((usage, index) => {
                  const status =
                    num(usage) >= 95
                      ? 'critical'
                      : num(usage) >= 85
                        ? 'warning'
                        : 'ok';

                  return (
                    <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.2,
                          borderRadius: 2,
                          height: '100%',
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          spacing={0.5}
                        >
                          <Typography variant="caption" fontWeight={900}>
                            CPU {index + 1}
                          </Typography>

                          <Typography variant="caption" fontWeight={900}>
                            {fmtPct(usage)}
                          </Typography>
                        </Stack>

                        <LinearProgress
                          variant="determinate"
                          value={clamp(usage)}
                          color={getProgressColor(status)}
                          sx={{
                            mt: 0.8,
                            height: 6,
                            borderRadius: 99,
                          }}
                        />
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Paper>
        </Box>

        {/* ================================================================= */}
        {/* Almacenamiento                                                     */}
        {/* ================================================================= */}

        <Box>
          <SectionTitle
            icon={<StorageIcon />}
            title="Almacenamiento"
            description="Particiones, uso de inodos y análisis dirigido de directorios."
          />

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(15, 23, 42, 0.10)',
              overflow: 'hidden',
            }}
          >
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Partición</TableCell>
                    <TableCell>Dispositivo</TableCell>
                    <TableCell align="right">Usado</TableCell>
                    <TableCell align="right">Disponible</TableCell>
                    <TableCell align="right">Inodos</TableCell>
                    <TableCell align="right">Estado</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {!filesystems.length && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" color="text.secondary">
                          No se encontraron datos de particiones.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  {filesystems.map((item) => (
                    <TableRow key={`${item.filesystem}-${item.path}`}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={900}>
                          {item.path || '—'}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {item.filesystem || '—'}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Stack spacing={0.5} alignItems="flex-end">
                          <Typography variant="body2" fontWeight={800}>
                            {fmtPct(item.used_percent, 1)}
                          </Typography>

                          <Typography variant="caption" color="text.secondary">
                            {item.used_h || fmtBytes(item.used)} /{' '}
                            {item.total_h || fmtBytes(item.total)}
                          </Typography>
                        </Stack>
                      </TableCell>

                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={800}>
                          {item.available_h || fmtBytes(item.available)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Stack spacing={0.5} alignItems="flex-end">
                          <Typography variant="body2" fontWeight={800}>
                            {fmtPct(item.inode_used_percent, 1)}
                          </Typography>

                          <Typography variant="caption" color="text.secondary">
                            {fmtInt(item.inode_used)} / {fmtInt(item.inode_total)}
                          </Typography>
                        </Stack>
                      </TableCell>

                      <TableCell align="right">
                        <StatusChip status={item.status || 'unknown'} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              mt: 2,
              p: 2.25,
              borderRadius: 3,
              border: '1px solid rgba(15, 23, 42, 0.10)',
            }}
          >
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={1.5}
                alignItems={{ lg: 'center' }}
                justifyContent="space-between"
              >
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FolderOpenIcon color="action" />

                    <Typography variant="subtitle1" fontWeight={900}>
                      Directorios que más espacio utilizan
                    </Typography>
                  </Stack>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    El análisis se ejecuta solo cuando lo solicitas. Así no consume
                    recursos en cada actualización del panel.
                  </Typography>
                </Box>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ sm: 'center' }}
                >
                  <TextField
                    select
                    size="small"
                    label="Ruta autorizada"
                    value={storagePath}
                    onChange={(event) => {
                      setStoragePath(event.target.value);
                      setStorageAnalysis(null);
                    }}
                    sx={{ minWidth: 220 }}
                    disabled={!allowedStoragePaths.length || storageLoading}
                  >
                    {allowedStoragePaths.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    size="small"
                    label="Resultados"
                    value={storageLimit}
                    onChange={(event) => {
                      setStorageLimit(num(event.target.value));
                      setStorageAnalysis(null);
                    }}
                    sx={{ minWidth: 120 }}
                    disabled={storageLoading}
                  >
                    {STORAGE_LIMIT_OPTIONS.map((item) => (
                      <MenuItem key={item} value={item}>
                        Top {item}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Button
                    variant="contained"
                    startIcon={
                      storageLoading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <FolderOpenIcon />
                      )
                    }
                    onClick={analyzeStorage}
                    disabled={!storagePath || storageLoading}
                    sx={{
                      minWidth: 170,
                      textTransform: 'none',
                      fontWeight: 800,
                    }}
                  >
                    {storageLoading ? 'Analizando…' : 'Analizar espacio'}
                  </Button>
                </Stack>
              </Stack>

              {storageAnalysis?.warning && (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  {storageAnalysis.warning}
                </Alert>
              )}

              {storageAnalysis?.error && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {storageAnalysis.message ||
                    'No fue posible analizar el almacenamiento.'}
                </Alert>
              )}

              {!storageAnalysis && !storageLoading && (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  Selecciona una ruta autorizada y presiona “Analizar espacio”.
                </Alert>
              )}

              {storageLoading && <LinearProgress />}

              {storageAnalysis && !storageAnalysis.error && (
                <>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ sm: 'center' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Ruta analizada: <strong>{storageAnalysis.path}</strong>
                    </Typography>

                    <Chip
                      size="small"
                      icon={<StorageIcon />}
                      label={`Total analizado: ${
                        storageAnalysis.scanned_h ||
                        fmtBytes(storageAnalysis.scanned_bytes)
                      }`}
                      variant="outlined"
                      sx={{ fontWeight: 800 }}
                    />
                  </Stack>

                  <Stack spacing={1.25}>
                    {!storageDirectories.length && (
                      <Typography variant="body2" color="text.secondary">
                        No se encontraron subdirectorios para mostrar.
                      </Typography>
                    )}

                    {storageDirectories.map((item) => (
                      <Paper
                        key={item.path}
                        variant="outlined"
                        sx={{ p: 1.4, borderRadius: 2 }}
                      >
                        <Stack spacing={0.8}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ minWidth: 0 }}
                            >
                              <FolderOpenIcon
                                fontSize="small"
                                color="action"
                              />

                              <Typography
                                variant="body2"
                                fontWeight={800}
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {item.path}
                              </Typography>
                            </Stack>

                            <Chip
                              size="small"
                              label={item.size_h || fmtBytes(item.bytes)}
                              variant="outlined"
                              sx={{ fontWeight: 800, flexShrink: 0 }}
                            />
                          </Stack>

                          <LinearProgress
                            variant="determinate"
                            value={clamp(item.percent_of_scanned_path)}
                            sx={{ height: 7, borderRadius: 99 }}
                          />

                          <Typography variant="caption" color="text.secondary">
                            {fmtPct(item.percent_of_scanned_path, 1)} de la ruta
                            analizada.
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </>
              )}
            </Stack>
          </Paper>
        </Box>

        {/* ================================================================= */}
        {/* Procesos                                                           */}
        {/* ================================================================= */}

        <Box>
          <SectionTitle
            icon={<DnsIcon />}
            title="Procesos del servidor"
            description="Procesos ordenados por uso de CPU y memoria al momento de la consulta."
          />

          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <ProcessTable
                title="Mayor uso de CPU"
                rows={processCpu}
                order="cpu"
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <ProcessTable
                title="Mayor uso de memoria"
                rows={processMemory}
                order="memory"
              />
            </Grid>
          </Grid>
        </Box>

        {/* ================================================================= */}
        {/* Base de datos                                                      */}
        {/* ================================================================= */}

        <Box>
          <SectionTitle
            icon={<StorageIcon />}
            title="Base de datos y plataforma"
            description="Tamaño, tenants, usuarios, permisos y datos operativos centrales."
          />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                icon={<StorageIcon />}
                title="Base central"
                value={
                  dbStats.central_db_size_h ||
                  fmtBytes(dbStats.central_db_size_bytes)
                }
                subtitle={`Tamaño de central_db · ${fmtInt(
                  dbTop.length
                )} bases detectadas`}
                status="ok"
                footer={`${fmtInt(
                  dbStats.central_db_size_bytes
                )} bytes`}
              />
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                icon={<ApartmentIcon />}
                title="Tenants"
                value={`${fmtInt(dbStats.tenants_active)} activos`}
                subtitle={`Total: ${fmtInt(dbStats.tenants_total)}`}
                status={
                  tenantActivePercent >= 95
                    ? 'ok'
                    : tenantActivePercent >= 80
                      ? 'warning'
                      : 'critical'
                }
                progress={tenantActivePercent}
                footer={`Actividad: ${fmtPct(tenantActivePercent, 1)}`}
              />
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                icon={<PeopleIcon />}
                title="Usuarios"
                value={`${fmtInt(dbStats.users_active)} activos`}
                subtitle={`Total: ${fmtInt(dbStats.users_total)} · Admins: ${fmtInt(
                  dbStats.admins_total
                )}`}
                status={
                  userActivePercent >= 95
                    ? 'ok'
                    : userActivePercent >= 80
                      ? 'warning'
                      : 'ok'
                }
                progress={userActivePercent}
                footer={`Usuarios activos: ${fmtPct(userActivePercent, 1)}`}
              />
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                icon={<SecurityIcon />}
                title="Accesos y permisos"
                value={`${fmtInt(dbStats.permissions_total)} permisos`}
                subtitle={`${fmtInt(
                  dbStats.role_permissions
                )} asignaciones de permisos`}
                status="ok"
                footer={`${fmtInt(
                  dbStats.account_tenant_roles
                )} relaciones cuenta-tenant`}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            <Grid item xs={12} lg={5}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(15, 23, 42, 0.10)',
                  overflow: 'hidden',
                  height: '100%',
                }}
              >
                <Box sx={{ px: 2.25, py: 1.75 }}>
                  <Typography variant="subtitle1" fontWeight={900}>
                    Bases de datos con mayor tamaño
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    Ranking entregado por PostgreSQL.
                  </Typography>
                </Box>

                <Divider />

                <Stack spacing={0} divider={<Divider />}>
                  {!dbTop.length && (
                    <Box sx={{ p: 2.25 }}>
                      <Typography variant="body2" color="text.secondary">
                        No hay datos de bases disponibles.
                      </Typography>
                    </Box>
                  )}

                  {dbTop.map((item, index) => (
                    <Stack
                      key={`${item.name}-${index}`}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ px: 2.25, py: 1.25 }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          size="small"
                          label={index + 1}
                          variant="outlined"
                          sx={{ fontWeight: 800 }}
                        />

                        <Typography variant="body2" fontWeight={800}>
                          {item.name || '—'}
                        </Typography>
                      </Stack>

                      <Chip
                        size="small"
                        label={item.size_h || fmtBytes(item.bytes)}
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 800 }}
                      />
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={7}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <MetricCard
                    icon={<HistoryIcon />}
                    title="Auditoría"
                    value={`${fmtInt(dbStats.audit_logs_since)} recientes`}
                    subtitle={`Ventana: ${meta.since || since}`}
                    status="ok"
                    footer={`Total histórico: ${fmtInt(
                      dbStats.audit_logs_total
                    )}`}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MetricCard
                    icon={<VpnKeyIcon />}
                    title="Tokens de sesión"
                    value={`${fmtInt(dbStats.refresh_tokens_since)} recientes`}
                    subtitle={`Ventana: ${meta.since || since}`}
                    status="ok"
                    footer={`Total almacenado: ${fmtInt(
                      dbStats.refresh_tokens_total
                    )}`}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MetricCard
                    icon={<BlockIcon />}
                    title="IPs bloqueadas"
                    value={fmtInt(dbStats.banned_ips_total)}
                    subtitle="Registros actualmente bloqueados."
                    status={num(dbStats.banned_ips_total) > 0 ? 'warning' : 'ok'}
                    footer="Revisar si existen bloqueos inesperados."
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MetricCard
                    icon={<AdminPanelSettingsIcon />}
                    title="Administradores"
                    value={fmtInt(dbStats.admins_total)}
                    subtitle="Usuarios con acceso administrativo."
                    status="ok"
                    footer={`Permisos registrados: ${fmtInt(
                      dbStats.permissions_total
                    )}`}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>

        {/* ================================================================= */}
        {/* Analítica                                                          */}
        {/* ================================================================= */}

        <Box>
          <SectionTitle
            icon={<PeopleIcon />}
            title="Distribución de usuarios y tenants"
            description="Resumen de asignaciones y tenants que requieren revisión."
          />

          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(15, 23, 42, 0.10)',
                  overflow: 'hidden',
                  height: '100%',
                }}
              >
                <Box sx={{ p: 2.25 }}>
                  <Typography variant="subtitle1" fontWeight={900}>
                    Usuarios con más asignaciones
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    Considera las relaciones cuenta-tenant existentes.
                  </Typography>
                </Box>

                <Divider />

                <Stack spacing={0} divider={<Divider />}>
                  {!topUsers.length && (
                    <Box sx={{ p: 2.25 }}>
                      <Typography variant="body2" color="text.secondary">
                        No hay asignaciones para mostrar.
                      </Typography>
                    </Box>
                  )}

                  {topUsers.map((item, index) => (
                    <Stack
                      key={`${item.name}-${index}`}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={1}
                      sx={{ p: 1.75 }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar
                          sx={{
                            width: 30,
                            height: 30,
                            fontSize: '0.8rem',
                            bgcolor: 'rgba(25, 118, 210, 0.12)',
                            color: '#1976d2',
                          }}
                        >
                          {index + 1}
                        </Avatar>

                        <Typography variant="body2" fontWeight={800}>
                          {item.name || 'Sin nombre'}
                        </Typography>
                      </Stack>

                      <Chip
                        size="small"
                        label={`${fmtInt(item.assignments)} asignaciones`}
                        variant="outlined"
                        sx={{ fontWeight: 800 }}
                      />
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(15, 23, 42, 0.10)',
                  overflow: 'hidden',
                  height: '100%',
                }}
              >
                <Box sx={{ p: 2.25 }}>
                  <Typography variant="subtitle1" fontWeight={900}>
                    Tenants sin usuarios asignados
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    Útil para detectar tenants creados pero aún no configurados.
                  </Typography>
                </Box>

                <Divider />

                {!tenantsWithoutUsers.length ? (
                  <Box sx={{ p: 2.25 }}>
                    <Alert severity="success" sx={{ borderRadius: 2 }}>
                      Todos los tenants tienen al menos un usuario asignado.
                    </Alert>
                  </Box>
                ) : (
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    useFlexGap
                    spacing={1}
                    sx={{ p: 2.25 }}
                  >
                    {tenantsWithoutUsers.map((tenant) => (
                      <Chip
                        key={tenant}
                        icon={<ApartmentIcon />}
                        label={tenant}
                        color="warning"
                        variant="outlined"
                        sx={{ fontWeight: 800 }}
                      />
                    ))}
                  </Stack>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* ================================================================= */}
        {/* Información técnica                                                */}
        {/* ================================================================= */}

        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3,
            border: '1px solid rgba(15, 23, 42, 0.10)',
          }}
        >
          <Stack spacing={1}>
            <Typography variant="subtitle2" fontWeight={900}>
              Umbrales activos del monitor
            </Typography>

            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.8}>
              {thresholdText.map((item) => (
                <Chip
                  key={item}
                  size="small"
                  label={item}
                  variant="outlined"
                  sx={{ fontWeight: 700 }}
                />
              ))}
            </Stack>

            <Typography variant="caption" color="text.secondary">
              Plataforma: {server.platform || '—'} {server.release || ''} ·
              Arquitectura: {server.arch || '—'} · CPU: {fmtInt(server.cpus)}{' '}
              núcleos · Análisis de directorios con caché de{' '}
              {fmtInt(meta.storage_cache_seconds || 300)} segundos.
            </Typography>
          </Stack>
        </Paper>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() =>
          setSnackbar((previous) => ({
            ...previous,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() =>
            setSnackbar((previous) => ({
              ...previous,
              open: false,
            }))
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}